import React, { useEffect } from "react";

const BlogLayout = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={styles.page}>
      {/* ===== Banner Section ===== */}
      <div style={styles.bannerSection}>
        <div style={styles.bannerOverlay}></div>
        <div style={styles.bannerContent}>
          <h1 style={styles.bannerTitle} className="slide-in">
            Securing Applications on the Cloud
          </h1>
          <p style={styles.bannerText} className="slide-in">
            Protecting Digital Assets with Zero-Trust, Encryption, and AI-Driven Security
          </p>
        </div>
      </div>

      {/* ===== Blog Container ===== */}
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.title}>
            Securing Applications on the Cloud: Building Trust in the Digital Era
          </h1>

          {/* Author & Meta */}
          <div style={styles.meta}>
            <img
              src="/images/author.jpg"
              alt="Author"
              style={styles.authorImage}
            />
            <div>
              <p style={styles.author}>By Sarah Williams</p>
              <p style={styles.details}>
                <i className="far fa-calendar-alt" style={styles.iconInline}></i>{" "}
                23 Aug 2025 &nbsp; | &nbsp;
                <i className="far fa-clock" style={styles.iconInline}></i> 9 min
                read &nbsp; | &nbsp;
                <i className="far fa-eye" style={styles.iconInline}></i> 1,850
                Views
              </p>
            </div>
          </div>

          {/* Blog Intro */}
          <p style={styles.text}>
            <i className="fas fa-lock" style={styles.iconInline}></i>
            As more organizations move their applications and data to the cloud,
            security becomes the top priority. Cloud platforms offer unmatched
            scalability and efficiency, but without the right protections,
            businesses risk data breaches, compliance failures, and reputational
            damage.
          </p>
          <p style={styles.text}>
            <i className="fas fa-shield-alt" style={styles.iconInline}></i>
            Securing cloud applications requires a layered approach—combining
            encryption, identity management, zero-trust frameworks, and continuous
            monitoring powered by AI.
          </p>

          {/* Why It Matters */}
          <h2 style={styles.subheading}>Why Securing Cloud Applications Matters</h2>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-database" style={styles.iconList}></i>
              Protects sensitive customer and business data
            </li>
            <li>
              <i className="fas fa-gavel" style={styles.iconList}></i>
              Ensures compliance with global regulations (GDPR, HIPAA, PCI-DSS)
            </li>
            <li>
              <i className="fas fa-user-shield" style={styles.iconList}></i>
              Builds customer trust and safeguards brand reputation
            </li>
            <li>
              <i className="fas fa-bug" style={styles.iconList}></i>
              Prevents ransomware, phishing, and insider threats
            </li>
          </ul>

          {/* Key Security Practices */}
          <h2 style={styles.subheading}>Key Practices for Securing Applications on Cloud</h2>
          <p style={styles.text}>
            Organizations adopting cloud-native applications should integrate the
            following security measures:
          </p>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-key" style={styles.iconList}></i>
              Strong Identity and Access Management (IAM)
            </li>
            <li>
              <i className="fas fa-lock" style={styles.iconList}></i>
              End-to-end encryption for data in transit and at rest
            </li>
            <li>
              <i className="fas fa-user-secret" style={styles.iconList}></i>
              Zero-Trust Architecture for least-privilege access
            </li>
            <li>
              <i className="fas fa-eye" style={styles.iconList}></i>
              Continuous monitoring with AI-driven threat detection
            </li>
          </ul>

          {/* Example */}
          <h2 style={styles.subheading}>Example: Securing a Healthcare Cloud Application</h2>
          <p style={styles.text}>
            <i className="fas fa-hospital" style={styles.iconInline}></i>
            In healthcare, cloud-based applications handle sensitive patient
            records. By applying multi-factor authentication, encryption, and
            compliance-driven monitoring, hospitals can ensure both security and
            regulatory compliance, while enabling doctors to access patient
            records securely from anywhere.
          </p>

          {/* Best Practices */}
          <h2 style={styles.subheading}>Best Practices for Cloud Security</h2>
          <p style={styles.text}>
            <i className="fas fa-lightbulb" style={styles.iconInline}></i>
            Companies securing their applications on cloud should:
          </p>
          <ul style={styles.list}>
            <li>
              <i className="fas fa-shield-virus" style={styles.iconList}></i>
              Regularly patch and update cloud workloads
            </li>
            <li>
              <i className="fas fa-cloud-upload-alt" style={styles.iconList}></i>
              Implement secure DevOps pipelines (DevSecOps)
            </li>
            <li>
              <i className="fas fa-network-wired" style={styles.iconList}></i>
              Use secure APIs and microservices with authentication
            </li>
            <li>
              <i className="fas fa-users-cog" style={styles.iconList}></i>
              Train employees to recognize social engineering attacks
            </li>
          </ul>

          {/* Future */}
          <h2 style={styles.subheading}>The Future of Cloud Security</h2>
          <p style={styles.text}>
            <i className="fas fa-globe" style={styles.iconInline}></i>
            By 2030, cloud security will rely heavily on AI-driven defense,
            blockchain-based identity verification, and fully automated
            compliance frameworks. Organizations that integrate these practices
            today will be well-positioned to thrive in a secure digital economy.
          </p>

          {/* Conclusion */}
          <h2 style={styles.subheading}>Conclusion</h2>
          <p style={styles.text}>
            <i className="fas fa-check-circle" style={styles.iconInline}></i>
            Securing cloud applications is not a one-time task but a continuous
            journey. Businesses must adopt zero-trust principles, invest in AI-driven
            monitoring, and train their workforce to proactively defend against
            threats. In doing so, they can unlock the full potential of the cloud
            while keeping security and trust at the center.
          </p>
        </div>
      </div>

      {/* ===== Animation CSS ===== */}
      <style>
        {`
          @keyframes slideIn {
            0% {
              opacity: 0;
              transform: translateX(-40px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in {
            animation: slideIn 1.2s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

const styles = {
  page: {
    position: "relative",
    background: "#fff",
    fontFamily: "Arial, sans-serif",
    letterSpacing: "0px",
  },
  /* ===== Banner Styles ===== */
  bannerSection: {
    position: "relative",
    width: "100%",
    height: "90vh",
    backgroundImage: "url('/image/secclo.avif')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0 10%",
    color: "#fff",
  },
  bannerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  bannerContent: {
    position: "relative",
    zIndex: 2,
    maxWidth: "600px",
  },
  bannerTitle: {
    fontSize: "42px",
    fontWeight: "bold",
    marginBottom: "15px",
    lineHeight: "1.3",
    letterSpacing:"0px",
    color:"white",
  },
  bannerText: {
    fontSize: "18px",
    lineHeight: "1.6",
    maxWidth: "500px",
    textAlign:"justify",
  },

  /* ===== Blog Styles ===== */
  container: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 20px",
    maxWidth: "900px",
    margin: "auto",
  },
  content: {
    flex: 1,
  },
  title: {
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "15px",
    lineHeight: "1.3",
    letterSpacing:"0px",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    gap: "12px",
  },
  authorImage: {
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  author: {
    margin: 0,
    fontWeight: "bold",
    fontSize: "15px",
  },
  details: {
    margin: 0,
    fontSize: "14px",
    color: "#666",
  },
  text: {
    fontSize: "16px",
    lineHeight: "1.6",
    color: "#333",
    marginBottom: "15px",
    textAlign: "justify",
  },
  subheading: {
    fontSize: "22px",
    fontWeight: "600",
    marginTop: "25px",
    marginBottom: "12px",
    letterSpacing:"0px",
  },
  list: {
    paddingLeft: "0px",
    marginBottom: "20px",
    listStyle: "none",
  },
  iconList: {
    marginRight: "8px",
    color: "#007bff",
  },
  iconInline: {
    marginRight: "6px",
    color: "#007bff",
  },

  /* ===== Responsive ===== */
  "@media (max-width: 768px)": {
    bannerTitle: {
      fontSize: "28px",
    },
    bannerText: {
      fontSize: "16px",
    },
    title: {
      fontSize: "26px",
    },
  },
};

export default BlogLayout;
